import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Brain, Heart, Users, Briefcase, 
  Palette, Globe, ThumbsDown, Smile, MessageCircle, Zap,
  Lightbulb, Network, Activity, Crown, Sparkles, Trash2
} from "lucide-react";
import { toast } from "sonner";

const PROFILE_CATEGORIES = [
  // Original 12 categories
  { id: "core_identity", label: "Core Identity", icon: Brain, description: "Who you are at your core, what feels most true to you, and how you naturally show up in the world" },
  { id: "communication_style", label: "Communication Style", icon: MessageCircle, description: "How you express yourself, share thoughts and feelings, and connect through words, tone, and presence" },
  { id: "love_language", label: "Love Language (Detailed)", icon: Heart, description: "How you give and receive affection, feel valued, and experience closeness in relationships" },
  { id: "lifestyle_interests", label: "Lifestyle & Interests", icon: Palette, description: "How you live day to day, what excites you, and the passions that shape your rhythm of life" },
  { id: "professional_work", label: "Professional & Work Life", icon: Briefcase, description: "How you approach work, purpose, ambition, and the role your career plays in your identity" },
  { id: "social_dynamics", label: "Social Dynamics", icon: Users, description: "How you interact with others, navigate groups, and build connection in social settings" },
  { id: "values_beliefs", label: "Values & Beliefs", icon: Sparkles, description: "What truly matters to you, the principles you live by, and the beliefs that guide your choices" },
  { id: "attractions_desires", label: "Attractions & Desires", icon: Heart, description: "What draws you in, sparks curiosity, and creates chemistry and longing" },
  { id: "dislikes_boundaries", label: "Dislikes & Boundaries", icon: ThumbsDown, description: "What feels misaligned, where your limits are, and what you need to feel safe and respected" },
  { id: "emotional_patterns", label: "Emotional Patterns", icon: Smile, description: "How you experience emotions, respond to stress, and move through emotional highs and lows" },
  { id: "aesthetic_expression", label: "Aesthetic & Expression", icon: Palette, description: "How you express yourself visually, creatively, and through personal style and presence" },
  { id: "cultural_background", label: "Cultural & Background", icon: Globe, description: "The experiences, heritage, and upbringing that have shaped who you are today" },
  // New 4 categories
  { id: "skills_aptitudes", label: "Skills & Aptitudes", icon: Lightbulb, description: "Core strengths, natural talents, and areas for growth or development" },
  { id: "relationship_ecosystem", label: "Relationship Ecosystem", icon: Network, description: "Key personal connections including family, friends, partners/ex-partners, children, co-parents, and animals/pets" },
  { id: "physical_mental_wellbeing", label: "Physical & Mental Wellbeing", icon: Activity, description: "Health considerations, psychological patterns, neurodiversity, and overall wellbeing factors" },
  { id: "leadership_orientation", label: "Leadership Orientation", icon: Crown, description: "Preferred leadership style, decision-making approach, and how you lead, follow, and guide others" },
];

export default function LearningResourcesManager({ ownerType, ownerId, ownerName, isAdmin }) {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSourceCount, setDataSourceCount] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await axios.get(`${API}/learning-profiles`, {
          params: { owner_type: ownerType, owner_id: ownerId },
          withCredentials: true,
        });
        setProfiles(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching learning profiles:", error);
        setLoading(false);
      }
    };

    const fetchDataSourceCount = async () => {
      try {
        const response = await axios.get(`${API}/data-sources`, {
          params: { owner_type: ownerType, owner_id: ownerId },
          withCredentials: true,
        });
        setDataSourceCount(response.data.length);
      } catch (error) {
        console.error("Error fetching data sources:", error);
      }
    };

    fetchProfiles();
    fetchDataSourceCount();
  }, [ownerType, ownerId]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/learning-profiles`, {
        params: { owner_type: ownerType, owner_id: ownerId },
        withCredentials: true,
      });
      setProfiles(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching learning profiles:", error);
      setLoading(false);
    }
  };

  const handleSetBestLearning = async () => {
    const existingProfiles = profiles.filter(p => p);
    
    if (existingProfiles.length === 0) {
      toast.error("No profiles exist yet. Generate profiles first.");
      return;
    }
    
    // Define optimal priorities per category (16 total)
    const optimalPriorities = {
      // Original 12 categories
      core_identity: 3,
      communication_style: 3,
      love_language: 3,
      values_beliefs: 3,
      attractions_desires: 3,
      dislikes_boundaries: 3,
      lifestyle_interests: 2,
      social_dynamics: 2,
      emotional_patterns: 2,
      professional_work: 1,
      aesthetic_expression: 1,
      cultural_background: 1,
      // New 4 categories
      skills_aptitudes: 2,
      relationship_ecosystem: 3,
      physical_mental_wellbeing: 3,
      leadership_orientation: 2
    };
    
    try {
      // Update each existing profile to optimal priority
      for (const profile of existingProfiles) {
        const optimalPriority = optimalPriorities[profile.profile_category] || 2;
        await axios.put(`${API}/learning-profiles/${profile.profile_id}`, {
          priority: optimalPriority
        }, { withCredentials: true });
      }
      
      toast.success("Optimized all profiles to best learning modes!");
      fetchProfiles();
    } catch (error) {
      console.error("Error setting best learning:", error);
      toast.error("Failed to optimize priorities");
    }
  };

  const handleAdminDeleteAll = async () => {
    if (!window.confirm(`Delete ALL learning library data for ${ownerName || "this person"}?\n\nThis will remove all learning profiles, resources, and source snapshots. This action cannot be undone.`)) {
      return;
    }
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/learning-profiles/all/${ownerType}/${ownerId}`, {
        withCredentials: true,
      });
      const data = response.data;
      toast.success(`Deleted ${data.profiles_deleted} profiles, ${data.resources_deleted} resources, ${data.snapshots_deleted} snapshots`);
      setProfiles([]);
      fetchProfiles();
    } catch (error) {
      console.error("Error deleting learning data:", error);
      toast.error(error.response?.data?.detail || "Failed to delete learning data");
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdatePriority = async (profileId, priority) => {
    try {
      await axios.put(`${API}/learning-profiles/${profileId}`, {
        priority: parseInt(priority)
      }, { withCredentials: true });

      toast.success("Priority updated");
      fetchProfiles();
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority");
    }
  };

  const getProfile = (categoryId) => {
    return profiles.find(p => p.profile_category === categoryId);
  };

  const getCoverageColor = (score) => {
    if (score >= 80) return "bg-green-500";
    if (score >= 60) return "bg-yellow-500";
    if (score >= 40) return "bg-orange-500";
    return "bg-red-500";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-cinzel text-xl text-white">
            {ownerType === "user" ? "What Agent Eros Knows About You" : `What Agent Eros Knows About ${ownerName}`}
          </h3>
          <p className="text-white/60 text-sm mt-1">
            {dataSourceCount} data source(s) available • {ownerType === "relationship" ? "Auto-imported from profile + manual sources" : "Manual sources"}
          </p>
        </div>
      </div>

      {/* Info Card for Relationships */}
      {ownerType === "relationship" && (
        <div className="glass-gold rounded-xl p-4">
          <p className="text-white/80 text-sm">
            💡 <strong>Auto-Import:</strong> Profile data, notes, pasted bio, and history are automatically available as data sources. Add more sources in the Data Sources tab for richer analysis.
          </p>
        </div>
      )}

      {/* Profile Selection */}
      <div className="glass-gold rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="text-white/80 text-sm">
                {PROFILE_CATEGORIES.length} Learning Profile Categories
              </span>
            </div>
            
            <div className="flex gap-2">
              <Button
                data-testid="delete-learning-btn"
                onClick={handleAdminDeleteAll}
                disabled={deleting}
                variant="ghost"
                size="icon"
                className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10"
                title="Delete all learning data"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                data-testid="best-learning-btn"
                onClick={handleSetBestLearning}
                variant="outline"
                className="border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
              >
                <Zap className="w-4 h-4 mr-2" />
                Best Learning
              </Button>
            </div>
          </div>

          {/* Profile List */}
          <div className="space-y-3">
            {PROFILE_CATEGORIES.map((category) => {
              const profile = getProfile(category.id);
              const isGenerated = !!profile;
              const coverage = profile?.coverage_score || 0;
              const priority = profile?.priority || 2;
              
              const getLearningModeLabel = (p) => {
                if (p === 1) return "Learning";
                if (p === 3) return "Advanced";  // Legacy value; kept as fallback for old rows.
                return "Moderate";
              };

              return (
                <div
                  key={category.id}
                  onClick={() => isGenerated && navigate(`/learning-profile/${profile.profile_id}`)}
                  className={`p-4 bg-white/5 rounded-lg transition-colors ${isGenerated ? 'hover:bg-white/10 cursor-pointer' : ''}`}
                >
                  {/* Desktop Layout */}
                  <div className="hidden md:flex items-center gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isGenerated ? 'bg-divine-gold/20' : 'bg-white/10'
                    }`}>
                      <category.icon className={`w-5 h-5 ${isGenerated ? 'text-divine-gold' : 'text-white/40'}`} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium">{category.label}</h4>
                      <p className="text-white/40 text-xs truncate">{category.description}</p>
                    </div>

                    {/* Coverage Bar */}
                    <div className="w-28">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-white/60">Coverage</span>
                        <span className={`text-xs font-medium ${isGenerated ? 'text-divine-gold' : 'text-white/30'}`}>
                          {isGenerated ? `${coverage}%` : '--'}
                        </span>
                      </div>
                      <div className="w-full bg-white/10 rounded-full h-2">
                        {isGenerated && (
                          <div
                            className={`h-2 rounded-full ${getCoverageColor(coverage)}`}
                            style={{ width: `${coverage}%` }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Learning Mode Selector */}
                    <div className="w-[140px]">
                      {isGenerated ? (
                        <Select
                          value={priority.toString()}
                          onValueChange={(value) => {
                            handleUpdatePriority(profile.profile_id, value);
                          }}
                        >
                          <SelectTrigger 
                            className="w-full bg-divine-black border-divine-gold/30 text-white text-xs"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-divine-charcoal border-divine-gold/30">
                            <SelectItem value="1">Learning</SelectItem>
                            <SelectItem value="2">Moderate</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-center">
                          <span className="text-white/30 text-xs">Not generated</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile Layout */}
                  <div className="md:hidden">
                    {/* Row 1: Icon + Title */}
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isGenerated ? 'bg-divine-gold/20' : 'bg-white/10'
                      }`}>
                        <category.icon className={`w-5 h-5 ${isGenerated ? 'text-divine-gold' : 'text-white/40'}`} />
                      </div>
                      <h4 className="text-white font-medium flex-1">{category.label}</h4>
                    </div>
                    
                    {/* Row 2: Coverage + Learning Mode */}
                    <div className="mt-2 pl-[52px] flex items-center gap-3 text-sm">
                      {isGenerated ? (
                        <>
                          <span className="text-white/60">Coverage</span>
                          <span className="text-divine-gold font-medium">{coverage}%</span>
                          <span className="text-white/30">•</span>
                          <Select
                            value={priority.toString()}
                            onValueChange={(value) => {
                              handleUpdatePriority(profile.profile_id, value);
                            }}
                          >
                            <SelectTrigger 
                              className="h-7 w-auto bg-transparent border-none text-cyan-400 text-sm p-0 gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-divine-charcoal border-divine-gold/30">
                              <SelectItem value="1">Learning</SelectItem>
                              <SelectItem value="2">Moderate</SelectItem>
                            </SelectContent>
                          </Select>
                        </>
                      ) : (
                        <span className="text-white/30">Not generated</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
    </div>
  );
}
